/**
 * The tag editor: a whole page for one song, or for several at once.
 *
 * Opened from "Edit tags" anywhere (the song menu, Ctrl+I, the selection bar,
 * "Review values"); Back returns to where you were. One song shows its cover,
 * every tag, Nova's own columns, BPM/key and playback points, and its history.
 * Several songs show the fields they share; where they differ the box says so
 * and stays empty, and only the fields you change are written -- to all of them.
 *
 * One Save (Ctrl+S) writes everything as one batch, so Undo and the history
 * treat it as one change.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { engineBase, fetchTrackDetail, type TracksPage, type TrackDetail } from "../api";
import { useApp } from "../core/app";
import { bus } from "../core/bus";
import { changeCover, removeCover } from "../core/commands";
import {
  announce,
  editableColumns,
  fetchHistory,
  fetchValues,
  fieldLabel,
  newBatch,
  parseValue,
  revertEntries,
  showValue,
  undoBatch,
  writeValues,
  type EditableColumn,
  type HistoryEntry,
} from "../core/edits";
import { player } from "../core/player";
import { markFixed, reasonLabel } from "../core/needsEdit";
import { songs } from "../format/count";
import { Artwork } from "../shell/Artwork";
import { Icon } from "../shell/Icons";
import { MOD } from "../shell/menus";
import { toast } from "../shell/Toast";
import { clockTime } from "../shell/Waveform";
import { StudioDock } from "../studio/StudioDock";
import "../studio/studio.css";
import type { Track } from "../types";
import { SongsView } from "./SongsView";

interface FieldState {
  /** The value all the songs share, or null. */
  value: unknown;
  /** The songs hold different values. */
  mixed: number;
}

function useSongs(ids: string[]) {
  const [tracks, setTracks] = useState<Track[] | null>(null);
  const key = ids.join(",");
  const load = useCallback(async () => {
    if (!ids.length) return setTracks([]);
    try {
      const res = await fetch(`${engineBase()}/api/tracks?ids=${key}&limit=1000&include=all`);
      const page = (await res.json()) as TracksPage;
      const byId = new Map(page.tracks.map((t) => [t.id, t]));
      setTracks(ids.map((id) => byId.get(id)).filter((t): t is Track => !!t));
    } catch {
      setTracks([]);
    }
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => void load(), [load]);
  // Flagged or marked fixed (here or on another machine): the banner follows.
  useEffect(() => bus.on("needsEdit.changed", () => void load()), [load]);
  return { tracks, reload: load };
}

export function TagEditorView() {
  const app = useApp();
  const raw = app.nav.params.ids ?? app.nav.params.id ?? "";
  const ids = useMemo(() => raw.split(",").filter(Boolean), [raw]);
  if (!ids.length) return <SongsView title="Tag editor" pickToEdit />;
  return <Editor key={raw} ids={ids} />;
}

function Editor({ ids }: { ids: string[] }) {
  const app = useApp();
  const { tracks, reload } = useSongs(ids);
  const one = tracks && tracks.length === 1 ? tracks[0] : null;
  const columns = useMemo(() => editableColumns(app.library.columns), [app.library.columns]);
  const [state, setState] = useState<Record<string, FieldState> | null>(null);
  const [detail, setDetail] = useState<TrackDetail | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [historyTick, setHistoryTick] = useState(0);
  const dock = useRef<{ save: () => Promise<void>; dirty: number } | null>(null);
  const [dockDirty, setDockDirty] = useState(0);

  // What the songs hold now: read from the file for one song, counted for several.
  const loadValues = useCallback(async () => {
    if (!tracks?.length) return;
    if (tracks.length === 1) {
      const d = await fetchTrackDetail(tracks[0].id);
      setDetail(d);
      const next: Record<string, FieldState> = {};
      for (const col of columns) {
        const v = col.kind === "column"
          ? tracks[0].custom?.[col.field] ?? null
          : (col.novaTag ? d?.nova?.[col.field] : d?.fields?.[col.field]) || null;
        next[col.id] = { value: v, mixed: 0 };
      }
      setState(next);
      return;
    }
    const answers = await Promise.all(columns.map((col) => fetchValues(col.id, tracks.map((t) => t.id))));
    const next: Record<string, FieldState> = {};
    columns.forEach((col, i) => {
      const values = answers[i].values;
      next[col.id] = values.length === 1 ? { value: values[0].value, mixed: 0 } : { value: null, mixed: values.length };
    });
    setState(next);
  }, [tracks, columns]);
  useEffect(() => void loadValues(), [loadValues]);

  const changes = useMemo(
    () =>
      columns
        .filter((c) => c.id in draft && draft[c.id] !== showValue(state?.[c.id]?.value))
        .map((col) => ({ col, ...parseValue(col, draft[col.id]) })),
    [columns, draft, state]
  );
  const invalid = changes.find((c) => c.error);
  const dirty = changes.length + dockDirty;

  const save = useCallback(async () => {
    if (!tracks?.length || saving) return;
    if (invalid) return toast(invalid.error!, "bad");
    setSaving(true);
    const batch = newBatch();
    let outcome = null;
    if (changes.length) {
      outcome = await writeValues(tracks.map((t) => t.id), changes.map((c) => ({ col: c.col, value: c.value })), batch);
    }
    if (dock.current?.dirty) await dock.current.save();
    setSaving(false);
    if (outcome) {
      announce(outcome, tracks.length === 1 ? `Saved “${tracks[0].title}”.` : `Saved ${songs(tracks.length)}.`, () => {
        void reload();
        setHistoryTick((n) => n + 1);
      });
      if (!outcome.error) setDraft({});
    }
    void loadValues();
    void app.library.refresh();
  }, [tracks, saving, invalid, changes, reload, loadValues, app.library]);

  // Ctrl+S saves the page; Alt+← / Alt+→ move to the previous or next song.
  const list = app.library.tracks;
  const at = one ? list.findIndex((t) => t.id === one.id) : -1;
  const step = useCallback(
    (delta: 1 | -1) => {
      const next = at >= 0 ? list[at + delta] : null;
      if (!next) return;
      if (dirty && !window.confirm("You have unsaved changes. Leave this song without saving them?")) return;
      app.navigate("studio", { ids: next.id });
    },
    [at, list, dirty, app]
  );
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        void save();
      } else if (e.altKey && (e.key === "ArrowLeft" || e.key === "ArrowRight")) {
        e.preventDefault();
        step(e.key === "ArrowLeft" ? -1 : 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [save, step]);

  const back = () => {
    if (dirty && !window.confirm("You have unsaved changes. Leave without saving them?")) return;
    app.back();
  };

  if (!tracks) return <section className="nv-view"><div className="nv-empty">Opening…</div></section>;
  if (!tracks.length) {
    return (
      <section className="nv-view">
        <div className="nv-empty">
          <b>Those songs are no longer in the library.</b>
          <button className="nv-btn" onClick={() => app.back()}>Back</button>
        </div>
      </section>
    );
  }

  const tags = columns.filter((c) => c.kind === "tag");
  const own = columns.filter((c) => c.kind === "column");
  const genres = app.library.genres.map((g) => g.name);

  return (
    <section className="nv-view nv-editor" aria-label="Tag editor">
      <header className="nv-editor-head">
        <button className="nv-btn" onClick={back} title="Back to where you were">
          <Icon name="back" /> Back
        </button>
        <div className="nv-editor-title">
          <h1 title={one?.title}>{one ? one.title : `${songs(tracks.length)}`}</h1>
          <div className="nv-sub">
            {one ? [one.artist, one.album].filter(Boolean).join(" · ") : "Fields they share are shown; changing a field changes it for all of them."}
          </div>
        </div>
        {one && (
          <span className="nv-editor-step">
            <button className="nv-iconbtn" disabled={at <= 0} title="Previous song (Alt+←)" onClick={() => step(-1)}><Icon name="prev" /></button>
            <button className="nv-iconbtn" disabled={at < 0 || at >= list.length - 1} title="Next song (Alt+→)" onClick={() => step(1)}><Icon name="next" /></button>
          </span>
        )}
        <span className={`nv-editor-status ${dirty ? "dirty" : ""}`}>{saving ? "Saving…" : dirty ? `${dirty} unsaved change${dirty === 1 ? "" : "s"}` : "Saved"}</span>
        <button className="nv-btn" disabled={!changes.length || saving} onClick={() => setDraft({})} title="Discard the changes you have not saved">Revert</button>
        <button className="nv-btn primary" disabled={!dirty || saving || !!invalid} onClick={() => void save()} title={`Save (${MOD}+S)`}>
          {saving ? "Saving…" : "Save"}
        </button>
      </header>

      <FlagBanner tracks={tracks} />

      <div className="nv-editor-body">
        <aside className="nv-editor-side">
          {one ? (
            <>
              <div className="nv-cover"><Artwork trackId={one.id} seed={one.album || one.artist || one.title} rounded={10} alt={`Cover of ${one.album ?? one.title}`} /></div>
              <div className="nv-editor-actions">
                <button className="nv-btn" onClick={() => player.playTracks([one])}><Icon name="play" /> Play</button>
                <button className="nv-btn" disabled={app.offline} onClick={async () => { if (await changeCover(one)) { void loadValues(); setHistoryTick((n) => n + 1); } }}>
                  {detail?.hasArtwork ? "Replace cover…" : "Add cover…"}
                </button>
                {detail?.hasArtwork && (
                  <button className="nv-btn" disabled={app.offline} onClick={async () => { if (await removeCover(one)) { void loadValues(); setHistoryTick((n) => n + 1); } }}>Remove cover</button>
                )}
              </div>
              <dl className="nv-kv nv-editor-file">
                <dt>Length</dt><dd>{clockTime(one.duration)}</dd>
                <dt>Format</dt><dd>{detail?.container ?? "…"}</dd>
                <dt>File</dt><dd className="nv-break">{detail?.path ?? one.path}</dd>
              </dl>
            </>
          ) : (
            <>
              <div className="nv-editor-stack">
                {tracks.slice(0, 4).map((t, i) => (
                  <span key={t.id} style={{ ["--i" as string]: i }}><Artwork trackId={t.id} seed={t.album || t.artist || t.title} size={120} rounded={8} /></span>
                ))}
              </div>
              <ol className="nv-editor-songs">
                {tracks.map((t) => (
                  <li key={t.id}>
                    <button className="nv-link" onClick={() => app.navigate("studio", { ids: t.id })} title="Edit only this song">{t.title}</button>
                    <span className="nv-dim">{t.artist}</span>
                  </li>
                ))}
              </ol>
              <p className="nv-dim">Covers are changed one song at a time: click a song above.</p>
            </>
          )}
        </aside>

        <div className="nv-editor-main">
          <section className="nv-editor-sec">
            <h2>Tags <span className="nv-dim">written into {tracks.length === 1 ? "the file" : "each file"}; the audio is never touched</span></h2>
            <div className="nv-editor-grid">
              {tags.map((col) => (
                <FieldBox key={col.id} col={col} state={state?.[col.id]} draft={draft[col.id]} onChange={(v) => setDraft((d) => ({ ...d, [col.id]: v }))} list={col.id === "genre" ? genres : undefined} />
              ))}
            </div>
          </section>

          {own.length > 0 && (
            <section className="nv-editor-sec">
              <h2>Your columns <span className="nv-dim">stored in Nova, kept with the audio</span></h2>
              <div className="nv-editor-grid">
                {own.map((col) => (
                  <FieldBox key={col.id} col={col} state={state?.[col.id]} draft={draft[col.id]} onChange={(v) => setDraft((d) => ({ ...d, [col.id]: v }))} />
                ))}
              </div>
            </section>
          )}

          {one && (
            <section className="nv-editor-sec">
              <h2>BPM, key and playback points <span className="nv-dim">where the song starts, and where it hands over to the next</span></h2>
              <StudioDock
                track={one}
                notation={app.notation}
                themeKey={app.theme.resolved}
                collapsed={false}
                onToggleCollapsed={() => undefined}
                onStep={() => false}
                onSaved={() => { void reload(); setHistoryTick((n) => n + 1); }}
                embedded
                controls={dock}
                onSaveShortcut={() => void save()}
                onDirtyChange={setDockDirty}
              />
            </section>
          )}

          {one && <History track={one} tick={historyTick} onReverted={() => { void reload(); void loadValues(); setDraft({}); }} />}
        </div>
      </div>
    </section>
  );
}

function FieldBox({ col, state, draft, onChange, list }: {
  col: EditableColumn; state?: FieldState; draft?: string; onChange: (v: string) => void; list?: string[];
}) {
  const current = showValue(state?.value);
  const value = draft ?? current;
  const changed = draft !== undefined && draft !== current;
  const error = changed ? parseValue(col, draft).error : undefined;
  const placeholder = state?.mixed ? `${state.mixed} different values — leave empty to keep each` : col.type === "date" ? "2024-05-31" : col.type === "boolean" ? "yes or no" : "";
  const props = {
    className: `nv-input ${changed ? "changed" : ""} ${error ? "bad" : ""}`,
    value: state?.mixed && draft === undefined ? "" : value,
    placeholder,
    disabled: !state,
    onChange: (e: { target: { value: string } }) => onChange(e.target.value),
  };
  return (
    <label className={`nv-field ${col.long ? "long" : ""}`}>
      <span>{col.label}{changed && <i className="nv-dirty-dot" title="Changed, not saved yet" />}</span>
      {col.long ? <textarea rows={3} {...props} /> : <input {...props} list={list ? `nv-list-${col.id}` : undefined} />}
      {list && <datalist id={`nv-list-${col.id}`}>{list.map((g) => <option key={g} value={g} />)}</datalist>}
      {error && <small className="nv-field-error">{error}</small>}
    </label>
  );
}

function History({ track, tick, onReverted }: { track: Track; tick: number; onReverted: () => void }) {
  const app = useApp();
  const [entries, setEntries] = useState<HistoryEntry[] | null>(null);
  const load = useCallback(() => void fetchHistory(track.id).then(setEntries), [track.id]);
  useEffect(load, [load, tick]);
  useEffect(() => bus.on("library.*", load), [load]);

  const batches = useMemo(() => {
    const out: { batch: string; at: string; source: string; entries: HistoryEntry[] }[] = [];
    for (const e of entries ?? []) {
      const last = out[out.length - 1];
      if (last && last.batch === e.batch) last.entries.push(e);
      else out.push({ batch: e.batch, at: e.at, source: e.source, entries: [e] });
    }
    return out;
  }, [entries]);

  const revert = async (run: () => Promise<string | null>) => {
    const error = await run();
    toast(error ? `Could not revert: ${error}` : "Reverted.", error ? "bad" : "ok");
    load();
    onReverted();
  };

  const when = (iso: string) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
  };
  const val = (e: HistoryEntry, v: unknown) =>
    e.kind === "cover" ? (v ? "a cover" : "no cover") : v === null || v === undefined || v === "" ? "empty" : `“${showValue(v)}”`;

  return (
    <section className="nv-editor-sec">
      <h2>History <span className="nv-dim">every change to this song; any of them can be put back</span></h2>
      {entries === null ? (
        <div className="nv-dim">Reading…</div>
      ) : !batches.length ? (
        <div className="nv-dim">No changes yet. Edits you make here, in the list or in the details panel appear here.</div>
      ) : (
        <ol className="nv-history">
          {batches.map((b) => {
            const open = b.entries.filter((e) => !e.revertedBy);
            return (
              <li key={b.batch}>
                <div className="nv-history-head">
                  <b>{when(b.at)}</b>
                  <span className="nv-dim">{b.source === "revert" ? "reverted" : b.source === "bulk" ? "with other songs" : "edited"}</span>
                  <span className="nv-spacer" />
                  {open.length > 0 ? (
                    <button className="nv-btn sm" disabled={app.offline} onClick={() => void revert(() => undoBatch(b.batch))}>
                      Revert{b.entries.length > 1 ? " all" : ""}
                    </button>
                  ) : (
                    <span className="nv-dim">put back</span>
                  )}
                </div>
                <ul>
                  {b.entries.map((e) => (
                    <li key={e.id} className={e.revertedBy ? "reverted" : ""}>
                      <span><b>{fieldLabel(e, app.library.columns)}</b> {val(e, e.old)} → {val(e, e.new)}</span>
                      {!e.revertedBy && b.entries.length > 1 && (
                        <button className="nv-link" disabled={app.offline} onClick={() => void revert(() => revertEntries([e.id]))}>Revert this</button>
                      )}
                    </li>
                  ))}
                </ul>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

/**
 * Songs open here that someone flagged for editing: why, their note, who -- and
 * Mark fixed once the edit is done (core/needsEdit). Editing alone does not clear
 * a flag; only a person can say the edit fixed what was wrong.
 */
function FlagBanner({ tracks }: { tracks: Track[] }) {
  const flagged = tracks.filter((t) => t.needsEdit);
  if (!flagged.length) return null;
  const first = flagged[0].needsEdit!;
  const one = flagged.length === 1;
  return (
    <div className={`nv-flag-banner ${flagged.some((t) => t.needsEdit!.reason === "dirty") ? "bad" : ""}`} role="status">
      <Icon name="bell" />
      <span>
        {one ? (
          <>
            <b>Needs edit: {reasonLabel(first.reason)}.</b>
            {first.note && <> {first.note}</>}
            <span className="nv-dim"> Flagged {new Date(first.at).toLocaleDateString()}{first.by ? ` by ${first.by}` : ""}.</span>
          </>
        ) : (
          <b>{songs(flagged.length)} here need editing: {[...new Set(flagged.map((t) => reasonLabel(t.needsEdit!.reason)))].join(", ")}.</b>
        )}
      </span>
      <span className="nv-spacer" />
      <button className="nv-btn" onClick={() => void markFixed(flagged.map((t) => t.id), one ? flagged[0].title : undefined)}>
        <Icon name="check" /> {one ? "Mark fixed" : `Mark ${flagged.length} fixed`}
      </button>
    </div>
  );
}
